import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { motion, AnimatePresence } from 'framer-motion';

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [platform, setPlatform] = useState(null); // 'ios', 'android', or null
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true
      || document.referrer.includes('android-app://');

    if (isStandalone) {
      return; // Already installed, don't show prompt
    }

    // Check if user dismissed the prompt recently
    const dismissedAt = localStorage.getItem('installPromptDismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return; // Don't show for 7 days after dismissal
      }
    }

    // Detect platform
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    const isAndroid = /android/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/chrome|crios|fxios/.test(userAgent);
    const isChrome = /chrome/.test(userAgent) && !/edge|edg/.test(userAgent);

    // iOS devices using Safari
    if (isIOS) {
      setPlatform('ios');
      // Delay showing the prompt to not interrupt initial page load
      setTimeout(() => setShowPrompt(true), 2500);
    } 
    // Android devices
    else if (isAndroid) {
      setPlatform('android');
      // For Android, listen for the beforeinstallprompt event
      const handleBeforeInstall = (e) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setShowPrompt(true), 2500);
      };
      window.addEventListener('beforeinstallprompt', handleBeforeInstall);
      
      // If no beforeinstallprompt fires within 5 seconds, show manual instructions
      const fallbackTimer = setTimeout(() => {
        if (!deferredPrompt) {
          setShowPrompt(true);
        }
      }, 5000);
      
      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
        clearTimeout(fallbackTimer);
      };
    }
  }, []);

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  const handleInstallAndroid = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
        localStorage.setItem('installPromptDismissed', Date.now().toString());
      }
      setDeferredPrompt(null);
    } else {
      // No native prompt available, just dismiss
      handleDismiss();
    }
  };

  if (!showPrompt || !platform) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe"
      >
        <div className="max-w-lg mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#006039] to-[#00915a] p-4 text-white">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl">
                  <Smartphone className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">App installieren</h3>
                  <p className="text-white/80 text-sm">Für schnelleren Zugriff</p>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                aria-label="Schließen"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {platform === 'ios' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Installiere AsceticLife auf deinem iPhone für ein App-ähnliches Erlebnis:
                </p>
                
                <div className="space-y-3">
                  {/* Step 1 */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#006039] text-white flex items-center justify-center text-sm font-medium">
                      1
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm">
                        Tippe auf das <strong>Teilen-Symbol</strong>
                      </p>
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                        <Share className="h-4 w-4 text-[#007AFF]" />
                        <span className="text-xs text-muted-foreground">in der Safari-Leiste</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#006039] text-white flex items-center justify-center text-sm font-medium">
                      2
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm">
                        Scrolle und wähle <strong>"Zum Home-Bildschirm"</strong>
                      </p>
                      <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-lg">
                        <Plus className="h-4 w-4 p-0.5 bg-slate-500 text-white rounded" />
                        <span className="text-xs text-muted-foreground">Zum Home-Bildschirm</span>
                      </div>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#006039] text-white flex items-center justify-center text-sm font-medium">
                      3
                    </div>
                    <div className="flex-1 pt-0.5">
                      <p className="text-sm">
                        Tippe auf <strong>"Hinzufügen"</strong> - Fertig!
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleDismiss}
                  variant="outline"
                  className="w-full mt-2"
                >
                  Verstanden
                </Button>
              </div>
            ) : platform === 'android' ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Installiere AsceticLife auf deinem Android-Gerät für schnellen Zugriff und Offline-Nutzung.
                </p>

                {deferredPrompt ? (
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleInstallAndroid}
                      className="w-full bg-gradient-to-r from-[#006039] to-[#00915a] hover:from-[#004d2e] hover:to-[#006039] gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Jetzt installieren
                    </Button>
                    <Button
                      onClick={handleDismiss}
                      variant="ghost"
                      className="w-full text-muted-foreground"
                    >
                      Später
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Manual instructions for Android */}
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#006039] text-white flex items-center justify-center text-sm font-medium">
                        1
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-sm">
                          Tippe auf das <strong>Menü</strong> (⋮) oben rechts
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#006039] text-white flex items-center justify-center text-sm font-medium">
                        2
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-sm">
                          Wähle <strong>"App installieren"</strong> oder <strong>"Zum Startbildschirm hinzufügen"</strong>
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleDismiss}
                      variant="outline"
                      className="w-full mt-2"
                    >
                      Verstanden
                    </Button>
                  </div>
                )}

                <p className="text-xs text-center text-muted-foreground">
                  Die App wird auf deinem Home-Bildschirm hinzugefügt
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InstallPrompt;
